"use client";

import React, { useMemo } from "react";
import Image from "next/image";

import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn, formatValue } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn/ui/tabs";

import { exchanges } from "./data";
import { useExchangeStats, useTopUsers } from "@/hooks/useLeaderboardData";

import { PageLeaderboardsSchema } from "@/sanity/pages/PageLeaderboardsTemplate";

import medal from "@/public/leaderboards/medal.svg";
import logo from "@/public/favicon.svg";

export default function LeaderboardsSection({
  data,
  className,
}: {
  data: PageLeaderboardsSchema;
  className?: string;
}) {
  // Fetch real exchange statistics from the platform API
  const { data: statsData } = useExchangeStats();

  // Fetch top users for each exchange (limited to 5 for the table)
  const bitunixTopUsers = useTopUsers("BITUNIX", 5);
  const pionexTopUsers = useTopUsers("PIONEX", 5);
  const phemexTopUsers = useTopUsers("PHEMEX", 5);

  // Merge real stats with static exchange metadata
  const exchangesWithRealData = useMemo(() => {
    if (!statsData) return exchanges;

    return exchanges.map((exchange) => {
      const realStats = statsData.find((stat) => stat.name === exchange.name);

      // Get top users for this exchange
      let topUsers = exchange.topUsers;
      if (exchange.name === "Bitunix" && bitunixTopUsers.data) {
        topUsers = bitunixTopUsers.data;
      } else if (exchange.name === "Pionex" && pionexTopUsers.data) {
        topUsers = pionexTopUsers.data;
      } else if (exchange.name === "Phemex" && phemexTopUsers.data) {
        topUsers = phemexTopUsers.data;
      }

      if (realStats) {
        return {
          ...exchange,
          totalPrize: realStats.totalPrize,
          totalWinners: realStats.totalWinners,
          usersCount: realStats.usersCount,
          topUsers: topUsers,
        };
      }
      return {
        ...exchange,
        topUsers: topUsers,
      };
    });
  }, [
    statsData,
    bitunixTopUsers.data,
    pionexTopUsers.data,
    phemexTopUsers.data,
  ]);

  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="relative overflow-clip bg-[#0A1A1D] pb-[8rem] lg:pb-[20rem] flex flex-col items-center">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
          style="light"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
          style="light"
        />

        <div className="relative">
          <DecorationSquares className="top-0" style="green" />

          <div className="container relative flex flex-col items-center pt-[13rem] lg:pt-[20rem] pb-[4rem] lg:pb-[6rem]">
            <Chip
              image={data?.leaderboardsChip?.icon}
              text={data?.leaderboardsChip?.text}
            />

            {data?.leaderboardsTexts && (
              <div className="richtext w-[min(77rem,100%)] prose-headings:text-white prose-p:text-white/70">
                <PortableText
                  value={data.leaderboardsTexts}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </div>

        <div className="w-full max-w-[149rem] flex flex-col items-center px-[2rem] lg:px-0">
          <Tabs
            defaultValue={exchangesWithRealData?.[0]?.name}
            className="w-full"
          >
            <div className="w-full flex justify-center items-center mb-[2rem]">
              <TabsList className="w-fit border border-white/10 p-[.5rem] !bg-transparent !rounded-none !h-auto items-stretch justify-stretch gap-[.25rem]">
                {exchangesWithRealData?.map((exchange, index) => (
                  <TabsTrigger
                    key={exchange.name + "-" + index}
                    value={exchange.name}
                    className="data-[state=active]:bg-white/5 data-[state=active]:border-white/10 text-white/60 data-[state=active]:text-white rounded-none h-[4rem] flex items-center justify-center gap-[1rem] px-6"
                  >
                    <span
                      className="w-[1.125rem] h-[1.125rem] rounded-full"
                      style={{ backgroundColor: exchange.color }}
                    />
                    <span className="font-[600] text-[1.875rem]">
                      Exchange {index + 1}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {exchangesWithRealData?.map((exchange) => (
              <TabsContent
                key={exchange.name}
                value={exchange.name}
                className="w-full"
              >
                <div className="bg-[#112628] p-[2rem] lg:p-[4rem]">
                  <div className="flex gap-[2rem] items-center mb-[3rem]">
                    <div className="w-[7rem] h-[7rem] min-w-[7rem] min-h-[7rem] bg-[#D9F9B9]/5 flex justify-center items-center">
                      <Image src={medal} alt="medal" />
                    </div>
                    <div className="flex flex-col">
                      <h4 className="text-white">Leaderboard</h4>
                      <span className="text-white/80 font-[400] text-[1.75rem] lg:text-[2rem]">
                        {data?.settings?.language === "en"
                          ? "Your position based on your volume"
                          : "Deine Position basierend auf deinem Volumen"}
                      </span>
                    </div>
                  </div>
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="text-white/80 text-[1.75rem]">
                        <th className="text-start !font-[500] !font-geist lg:ps-[2rem] pe-[1rem] lg:pe-0">
                          #
                        </th>
                        <th className="text-start !font-[500] !font-geist">
                          NAME
                        </th>
                        <th className="text-end !font-[500] !font-geist">
                          CASHBACK
                        </th>
                        <th className="text-end !font-[500] !font-geist lg:pe-[2rem]">
                          TRADING VOLUME
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {exchange?.topUsers.map((user, index) => (
                        <tr
                          key={index + 100}
                          className="group border-b border-b-white/10 last:border-none hover:bg-white/5 transition-colors"
                        >
                          <td className="text-white/50 !font-[400] !font-geist text-[2rem] group-hover:text-white transition-colors text-start lg:ps-[2rem] py-[1rem] pe-[1rem] lg:pe-0">
                            {index + 1}
                          </td>

                          <td className="text-white/50 !font-[400] !font-geist text-[2rem] group-hover:text-white transition-colors py-[1rem] flex items-center gap-[1rem] leading-[100%]">
                            <Image
                              src={logo}
                              width={32}
                              height={32}
                              alt="avatar"
                              className="rounded-full overflow-clip opacity-80 group-hover:opacity-100 transition-opacity min-w-[4rem] min-h-[4rem] max-w-[4rem] max-h-[4rem] lg:min-w-[5rem] lg:min-h-[5rem] lg:max-w-[5rem] lg:max-h-[5rem]"
                            />
                            {user.name}
                          </td>
                          <td className="text-white/50 !font-[400] !font-geist text-[2rem] group-hover:text-white transition-colors text-end py-[1rem]">
                            $ {formatValue(user.cashback, 0)}
                          </td>
                          <td className="text-white/50 !font-[400] !font-geist text-[2rem] group-hover:text-white transition-colors text-end py-[1rem] lg:pe-[2rem]">
                            $ {formatValue(user.tradingVolume, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      <div className="relative overflow-clip">
        {data?.participateScroll && (
          <ScrollingBanner text={data.participateScroll} />
        )}

        <DecorationLine className="bottom-0 left-0" orientation="horizontal" />
      </div>
    </section>
  );
}
